import type { ComponentPropsWithRef } from 'react'
import { Icon, type IconNameEnum } from '@/React/Shared/Component/Icon'

export interface CardContainerProps extends ComponentPropsWithRef<'div'> {
    noColors?: boolean
    paddingSize?: 'none' | 'small' | 'medium' | 'large'
    isActive?: boolean
    shouldHighlightOnHover?: boolean
    isHighlighted?: boolean
}

const paddingClasses = {
    none: 'p-0',
    small: 'p-2',
    medium: 'p-4',
    large: 'p-6',
}

function CardContainer({
    noColors = false,
    paddingSize = 'large',
    isActive = false,
    shouldHighlightOnHover = false,
    isHighlighted = false,
    children,
    className = '',
    ...divProps
}: CardContainerProps) {
    const classes = [
        'rounded-xl h-full w-full space-y-4 flex flex-col',
        paddingClasses[paddingSize],
    ]

    if (!noColors) {
        classes.push(
            isActive
                ? 'bg-primary-shade-ligth border border-transparent ring-2 ring-inset ring-primary shadow-xs'
                : 'bg-white border border-stone-200 shadow-xs',
        )
    }

    if (shouldHighlightOnHover) {
        classes.push('hover:ring-2 hover:ring-inset hover:ring-primary')
    }

    if (isHighlighted) {
        classes.push('ring-2 ring-inset ring-primary')
    }

    classes.push(className)

    return (
        <div
            {...divProps}
            className={classes.join(' ')}
        >
            {children}
        </div>
    )
}

export interface CardTitleProps extends Omit<
    ComponentPropsWithRef<'div'>,
    'title'
> {
    iconName?: IconNameEnum | null
    iconClass?: string
}

function CardTitle({
    children,
    iconName,
    iconClass = 'w-4 h-4 text-gray-600',
    className = '',
    ...divProps
}: CardTitleProps) {
    return (
        <div
            {...divProps}
            className={`flex items-center gap-2 text-gray-600 ${className}`}
        >
            {iconName ? (
                <Icon
                    name={iconName}
                    className={iconClass}
                    aria-hidden="true"
                />
            ) : null}
            <div className="font-semibold">{children}</div>
        </div>
    )
}

function CardContent({
    children,
    className = '',
    ...divProps
}: ComponentPropsWithRef<'div'>) {
    return (
        <div
            {...divProps}
            className={`flex-1 items-stretch ${className}`}
        >
            {children}
        </div>
    )
}

function CardActions({
    children,
    className = '',
    ...divProps
}: ComponentPropsWithRef<'div'>) {
    return (
        <div
            {...divProps}
            className={`flex justify-end ${className}`}
        >
            {children}
        </div>
    )
}

export const Card = {
    Container: CardContainer,
    Title: CardTitle,
    Content: CardContent,
    Actions: CardActions,
}
